import { useCallback, useEffect, useState, useRef } from "react";
import { useAtomValue } from "jotai";
import { Icon } from "@zapier/zinnia-icons";
import { useCopilotChat } from "@zapier/ai-toolkit/client";

import {
  isBlankCanvasAtom,
  isDraftModeIncludingTemplatesAtom,
  nodesAtom,
} from "@zapier/canvas-state";
import { BuildModeSuggestionType } from "@zapier/copilot-tools";

type IconName = React.ComponentProps<typeof Icon>["name"];

import styles from "./PersistentSuggestionButtons.module.css";

const promptMapping = {
  [BuildModeSuggestionType.SETUP_ZAPS]:
    "Help me configure field mapping for the Zaps on this canvas. Please work through them one at a time and ask for any additional information you need.",
  [BuildModeSuggestionType.LEAD_GENERATION]:
    "Build a lead generation system for me.",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]:
    "Build a customer support automation for me.",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]:
    "Build an e-commerce order processing system for me.",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "Use the tour tool",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]:
    "Explain what I have built on this canvas and how it works.",
  [BuildModeSuggestionType.WHAT_CAN_COPILOT_DO]:
    "What can Canvas Copilot do? Tell me about all the capabilities and features available.",
  [BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES]:
    "What are common use cases for Zapier? Show me some popular automation examples and workflows.",
  [BuildModeSuggestionType.LEARN_ABOUT_CANVAS]:
    "Teach me about Canvas and how to use it effectively for building automations.",
  [BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS]:
    "I'm new to Zapier. Can you teach me the basics and help me understand how to get started?",
};

const iconMapping: Record<BuildModeSuggestionType, IconName> = {
  [BuildModeSuggestionType.SETUP_ZAPS]: "miscBoltAlt",
  [BuildModeSuggestionType.LEAD_GENERATION]: "miscLearn",
  [BuildModeSuggestionType.CUSTOMER_SUPPORT]: "miscCopilot",
  [BuildModeSuggestionType.ECOMMERCE_ORDER]: "miscLearn",
  [BuildModeSuggestionType.TOUR_MY_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.EXPLAIN_MY_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.WHAT_CAN_COPILOT_DO]: "miscCopilot",
  [BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES]: "miscBoltAlt",
  [BuildModeSuggestionType.LEARN_ABOUT_CANVAS]: "navCanvas",
  [BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS]: "miscLearn",
  [BuildModeSuggestionType.CREATE_AUTH]: "miscCopilot",
};

export function PersistentSuggestionButtons() {
  const { setMessages, reload, messages } = useCopilotChat();
  const isBlankCanvas = useAtomValue(isBlankCanvasAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const nodes = useAtomValue(nodesAtom);
  const [showButtons, setShowButtons] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const baselineInputHeightRef = useRef<number | null>(null);

  // Hide buttons when user adds new messages to the conversation
  useEffect(() => {
    if (messages && messages.length > 0 && !hasUserInteracted) {
      // Check if there are any user messages (excluding initial assistant messages)
      const hasUserMessages = messages.some((msg) => msg.role === "user");
      if (hasUserMessages) {
        setHasUserInteracted(true);
        setShowButtons(false);
      }
    }
  }, [messages, hasUserInteracted]);

  // Once user has interacted, always hide buttons
  useEffect(() => {
    if (hasUserInteracted && showButtons) {
      setShowButtons(false);
    }
  }, [hasUserInteracted, showButtons]);

  // Position buttons based on chat input height
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const updateButtonPosition = () => {
      if (!containerRef.current) return;

      // Find the parent floatingSuggestionButtons div that has the actual positioning
      const floatingButtonsContainer = containerRef.current.closest(
        '[class*="floatingSuggestionButtons"]',
      ) as HTMLElement;

      if (!floatingButtonsContainer) {
        return;
      }

      // Look for visible textarea elements that are likely the chat input
      const textareas = document.querySelectorAll("textarea");
      let chatInput: HTMLTextAreaElement | null = null;

      // Find the textarea that's actually visible and has meaningful dimensions
      for (const textarea of textareas) {
        const rect = textarea.getBoundingClientRect();
        if (rect.height > 0 && rect.width > 0 && rect.bottom > 0) {
          chatInput = textarea as HTMLTextAreaElement;
          break;
        }
      }

      if (chatInput) {
        const inputRect = chatInput.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Only reposition if the input is actually visible and positioned
        if (
          inputRect.height > 20 &&
          inputRect.bottom > 0 &&
          inputRect.bottom < viewportHeight
        ) {
          // Set baseline height on first measurement ONLY
          if (baselineInputHeightRef.current === null) {
            baselineInputHeightRef.current = inputRect.height;
            floatingButtonsContainer.style.bottom = "120px";

            // Set up ResizeObserver to watch for input size changes
            resizeObserver = new ResizeObserver(() => {
              updateButtonPosition();
            });
            resizeObserver.observe(chatInput);

            // Also listen for input events as backup
            const handleInputChange = () => {
              updateButtonPosition();
            };
            chatInput.addEventListener("input", handleInputChange);
            chatInput.addEventListener("paste", handleInputChange);

            return;
          }

          // Calculate movement based on growth from the FIXED baseline
          const extraHeight = Math.max(
            0,
            inputRect.height - baselineInputHeightRef.current,
          );
          const defaultPosition = 120;
          // When input grows, buttons move UP by the exact same amount to maintain consistent spacing
          const desiredBottom = defaultPosition + extraHeight;

          floatingButtonsContainer.style.bottom = `${desiredBottom}px`;
        } else {
          // Reset to default position if input is not properly positioned
          floatingButtonsContainer.style.bottom = "120px";
        }
      } else {
        // Reset to default if no valid input found
        floatingButtonsContainer.style.bottom = "120px";
      }
    };

    // Initial positioning
    updateButtonPosition();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [messages, showButtons]);

  // Determine which suggestions to show based on canvas state
  const getSuggestions = useCallback(() => {
    // Show 4 default prompts for blank/new canvas (regardless of draft mode)
    if (isBlankCanvas) {
      return [
        BuildModeSuggestionType.WHAT_CAN_COPILOT_DO,
        BuildModeSuggestionType.ZAPIER_COMMON_USE_CASES,
        BuildModeSuggestionType.LEARN_ABOUT_CANVAS,
        BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS,
      ];
    }

    // Show contextual suggestions for canvas with content but not in draft mode
    if (!isDraftModeIncludingTemplates) {
      // Check if there are any zap nodes on the canvas
      const hasZapNodes = nodes.some((node) => node.type === "zap");

      // Base suggestions for any non-blank canvas (always 4 buttons)
      const suggestions = [
        BuildModeSuggestionType.TOUR_MY_CANVAS,
        BuildModeSuggestionType.EXPLAIN_MY_CANVAS,
        BuildModeSuggestionType.WHAT_CAN_COPILOT_DO,
      ];

      // Add Setup My Zaps if there are zap nodes, otherwise add a general help option
      if (hasZapNodes) {
        suggestions.push(BuildModeSuggestionType.SETUP_ZAPS);
      } else {
        suggestions.push(BuildModeSuggestionType.ZAPIER_FOR_BEGINNERS);
      }

      return suggestions;
    }

    // Default case - no buttons
    return [];
  }, [isBlankCanvas, isDraftModeIncludingTemplates, nodes]);

  const handleSuggestionClick = useCallback(
    async (suggestion: BuildModeSuggestionType) => {
      // Mark that user has interacted and hide all buttons immediately
      setHasUserInteracted(true);
      setShowButtons(false);

      const promptText =
        promptMapping[suggestion as keyof typeof promptMapping] || suggestion;

      // Use setMessages to append user message to conversation
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "user",
          content: promptText,
          id: `user-${Date.now()}`,
          createdAt: new Date(),
          parts: [{ type: "text", text: promptText }],
        },
      ]);

      // Trigger LLM response after adding the user message
      try {
        await reload();
      } catch (error) {
        console.error("Failed to reload chat after suggestion click:", error);
      }
    },
    [setMessages, reload],
  );

  const suggestions = getSuggestions();

  // Don't render if buttons should be hidden or no suggestions
  if (!showButtons || suggestions.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className={styles.container}>
      {suggestions.map((suggestion) => {
        const iconName = iconMapping[suggestion as keyof typeof iconMapping];
        return (
          <button
            key={suggestion}
            onClick={() => handleSuggestionClick(suggestion)}
            className={styles.suggestionButton}
          >
            <Icon
              name={iconName}
              size="20"
              color="UiPrimaryStronger"
              className={styles.buttonIcon}
            />
            <span className={styles.buttonText}>{suggestion}</span>
          </button>
        );
      })}
    </div>
  );
}
